import { FC, ReactNode } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import LOCALES from '~/locales';
import { useLocale } from '~/hooks/useLocale';
import { useTranslation } from 'react-i18next';

export const SITE_URL = 'https://bitecritic.com';
export const SITE_LOGO = `${SITE_URL}/icons/icon-192x192.webp`;

export interface SEOBaseProps {
  title?: string;
  description?: string;
  image?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  nofollow?: boolean;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  ogType?: string;
  children?: ReactNode;
  structuredData?: string;
  noTitle?: boolean;
  noDescription?: boolean;
}

const DEFAULT_IMAGE = '/images/hero-bg.webp';

export const SEOBase: FC<SEOBaseProps> = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  canonicalUrl,
  noindex = false,
  nofollow = false,
  keywords,
  author,
  publishedTime,
  modifiedTime,
  ogType = 'website',
  children,
  structuredData = '{}',
  noTitle = false,
  noDescription = false,
}) => {
  const router = useRouter();
  const { currentLocale } = useLocale();
  const { t } = useTranslation();

  const SITE_NAME = t('seo.meta.siteName', 'BiteCritic');
  const DEFAULT_DESCRIPTION = t('seo.meta.defaultDescription', 'Discover and review the best food and restaurants near you');
  const DEFAULT_KEYWORDS = t('seo.meta.defaultKeywords', 'food, restaurants, reviews, dining, cuisine, food critics');

  const fullTitle = title ? `${title} | ${SITE_NAME}` : t('seo.meta.defaultTitle', 'BiteCritic - Discover and Share Food Experiences');
  const currentUrl = `${SITE_URL}/${currentLocale.code}${router.asPath}`;
  const finalCanonicalUrl = canonicalUrl || currentUrl;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  const finalKeywords = keywords || DEFAULT_KEYWORDS;
  const finalAuthor = author || SITE_NAME;

  // Generate alternate language URLs
  const alternateLanguages = Object.values(LOCALES).reduce((acc, locale) => ({
    ...acc,
    [locale.code]: `${SITE_URL}/${locale.code}${router.asPath}`,
  }), {} as Record<string, string>);

  const shortDescription = finalDescription.length > 152 ? finalDescription.substring(0, 152) + '...' : finalDescription;

  return (
    <Head>
      {/* Basic Meta Tags */}
      {!noTitle && <title>{fullTitle}</title>}
      {!noDescription && <meta name="description" content={shortDescription} />}
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={finalAuthor} />
      <meta name="robots" content={`${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`} />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content={currentLocale.language} />
      <link rel="canonical" href={finalCanonicalUrl} />

      {/* Favicon and App Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/webp" sizes="48x48" href="/icons/icon-48x48.webp" />
      <link rel="icon" type="image/webp" sizes="72x72" href="/icons/icon-72x72.webp" />
      <link rel="icon" type="image/webp" sizes="96x96" href="/icons/icon-96x96.webp" />
      <link rel="icon" type="image/webp" sizes="144x144" href="/icons/icon-144x144.webp" />
      <link rel="icon" type="image/webp" sizes="192x192" href="/icons/icon-192x192.webp" />
      <link rel="icon" type="image/webp" sizes="256x256" href="/icons/icon-256x256.webp" />
      <link rel="icon" type="image/webp" sizes="384x384" href="/icons/icon-384x384.webp" />
      <link rel="icon" type="image/webp" sizes="512x512" href="/icons/icon-512x512.webp" />
      <link rel="manifest" href="/site.webmanifest" />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={shortDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentLocale.code} />
      <meta property="og:logo" content={SITE_LOGO} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={shortDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:site" content="@bitecritic" />
      <meta name="twitter:creator" content="@bitecritic" />

      {/* Mobile App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="format-detection" content="telephone=no" />

      {/* Alternate Language Tags */}
      {Object.entries(alternateLanguages).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      {/* x-default hreflang */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_URL}${router.asPath}`}
      />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structuredData }}
      />

      {/* Additional custom meta tags */}
      {children}
    </Head>
  );
};

export default SEOBase; 